import { create15Times, Schedule } from '@raysee/schema';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button, ControlLabel, Form, FormGroup, HelpBlock, Modal, SelectPicker } from 'rsuite';

interface Props {
  isOpen: boolean;
  selectedData?: Date;
  currentSchedules: Schedule[];
  onClose: () => void;
  onCreate: (props: Pick<Schedule, 'startTime' | 'endTime'>) => void;
}

interface FieldValues {
  startTime: number;
  endTime: number;
}

export const CreateScheduleModal = (props: Props) => {
  const {
    control,
    getValues,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FieldValues>({
    defaultValues: {
      startTime: undefined,
      endTime: undefined,
    },
  });

  const selectedStartTime = watch('startTime');
  const selectedEndTime = watch('endTime');

  // 両方の時間が選択されているかを確認
  const isFormValid = selectedStartTime != null && !!selectedEndTime;

  // モーダルが開いたときにフォームをリセット
  useEffect(() => {
    if (props.isOpen) {
      reset({
        startTime: undefined,
        endTime: undefined,
      });
    }
  }, [props.isOpen, reset]);

  // 開始時間が変更されたら終了時間をリセット
  useEffect(() => {
    if (selectedStartTime != null) {
      setValue('endTime', undefined);
    }
  }, [selectedStartTime, setValue]);

  // 開始時間の設定
  const now = dayjs();
  const selectedDate = props.selectedData ? dayjs(props.selectedData) : dayjs();
  const isToday = selectedDate.isSame(now, 'day');

  let startStarted;
  if (isToday) {
    // 今日の場合は現在時刻以降（15分単位に切り上げ）
    const currentMinutes = now.hour() * 60 + now.minute();
    const roundedUpMinutes = Math.ceil(currentMinutes / 15) * 15;
    startStarted = dayjs().startOf('day').add(roundedUpMinutes, 'minutes');
  } else {
    // 今日以外の場合は00:00から
    startStarted = dayjs().startOf('day');
  }

  const StartEnded = dayjs().startOf('day').add(1, 'day').subtract(15, 'minute');
  const startTimes = create15Times(startStarted, StartEnded);

  // 終了は00:15~翌06:00
  const endStarted = dayjs().startOf('day').add(15, 'minute');
  const endEnded = dayjs().startOf('day').add(1, 'day').add(6, 'hour');
  const endTimes = create15Times(endStarted, endEnded);

  const filteredEndTimes =
    selectedStartTime != null
      ? endTimes.filter((time) => {
          // 開始時間より大きい値のみを表示
          if (time.value <= selectedStartTime) return false;

          // 開始時間と同じ分数のものだけを表示（例：10:15なら11:15, 12:15など）
          const startMinutes = selectedStartTime % 60;
          const endMinutes = time.value % 60;
          return startMinutes === endMinutes;
        })
      : [];

  const onClickSubmit = () => {
    if (!props.selectedData) return;
    const { startTime, endTime } = getValues();

    if (startTime >= endTime) {
      alert('終了時間は開始時間以降にしてください');
      return;
    }
    if (endTime - startTime < 60) {
      alert('1時間以上のシフトを登録してください');
      return;
    }
    const start = dayjs(props.selectedData).startOf('day').add(startTime, 'minutes');
    const end = dayjs(props.selectedData).startOf('day').add(endTime, 'minutes');
    if (start.isBefore(dayjs())) {
      alert('過去のシフトは登録できません');
      return;
    }

    if (endTime - startTime > 24 * 60) {
      alert('最大24時間までとなります');
      return;
    }

    props.onCreate({
      startTime: start.toDate(),
      endTime: end.toDate(),
    });
  };

  return (
    <Modal show={props.isOpen} onHide={props.onClose} size="xs">
      <Modal.Header>
        <Modal.Title>
          スケジュール追加
          {props.selectedData ? dayjs(props.selectedData).format('YYYY/MM/DD') : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form fluid layout="inline">
          <FormGroup>
            <ControlLabel>開始</ControlLabel>
            <Controller
              name="startTime"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <SelectPicker
                  className="min-w-60"
                  data={startTimes}
                  placeholder="HH:MM"
                  searchable={false}
                  {...field}
                />
              )}
            />
            {errors.startTime && <HelpBlock>{errors.startTime}</HelpBlock>}
          </FormGroup>
          <FormGroup>
            <ControlLabel>終了</ControlLabel>
            <Controller
              name="endTime"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <SelectPicker
                  data={filteredEndTimes}
                  placeholder="HH:MM"
                  searchable={false}
                  disabled={selectedStartTime == null}
                  locale={{ noResultsText: '開始時間を選んでください' }}
                  {...field}
                />
              )}
            />
            {errors.endTime && <HelpBlock>{errors.endTime}</HelpBlock>}
          </FormGroup>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClickSubmit} appearance="primary" color="blue" disabled={!isFormValid}>
          追加する
        </Button>
        <Button onClick={props.onClose} appearance="subtle">
          キャンセル
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
